import { useMemo, useState } from 'react';
import { arrayOf, bool, func, node, object, shape, string } from 'prop-types';
import { defaults, noop, sortBy } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import { darken, lighten, alpha } from '@material-ui/core/styles/colorManipulator';
import { Divider, IconButton, List, ListItem, ListItemText, Popover, Tooltip } from '@material-ui/core';
import {
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    HelpOutline as HelpOutlineIcon,
    RadioButtonUnchecked as RadioButtonUncheckedIcon,
    Save as SaveIcon,
} from '@material-ui/icons';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import { middleEllipsis } from '../../../utilities/StringUtilities';
import { AddFilter as AddFilterIcon, CheckCircleOff as CheckCircleOffIcon } from '../../icon/Icons';
import Actions, { Action, Spacer } from '../actions/Actions';
import { useDashboardContext } from '../dashboard/DashboardContext';

const useStyles = makeStyles((theme) => {
    const isLightTheme = theme.palette.type === 'light';
    const hoverColor = isLightTheme
        ? darken(alpha(theme.palette.background.paper, 1), 0.03)
        : lighten(alpha(theme.palette.background.paper, 1), 0.06);

    return {
        root: {
            '& .MuiIconButton-root:hover': {
                background: 'none',
            },
        },
        menu: {
            '& .MuiListItem-root': {
                cursor: 'pointer',

                '&:hover': {
                    backgroundColor: hoverColor,
                },
            },

            '& .MuiListItemText-primary': {
                ...theme.mixins.flex.displayFlex,
                ...theme.mixins.flex.alignItemsCenter,
                fontSize: 15,
                fontWeight: 400,
                color: theme.palette.text.primary,

                '& a': {
                    display: 'block',
                    fontWeight: 400,
                    color: theme.palette.text.primary,
                },

                '& svg': {
                    fontSize: 20,
                    margin: 'auto',
                    display: 'block',
                },
            },
        },
        headerItem: {
            '&.MuiListItem-root': {
                cursor: 'default',

                '&:hover': {
                    backgroundColor: 'inherit',
                },
            },

            '& .MuiListItemText-primary': {
                color: theme.palette.text.secondary,
            },
        },
        itemActions: {
            marginLeft: theme.spacing(2),
        },
        helpTooltipContainer: {
            ...theme.mixins.flex.displayFlex,
            ...theme.mixins.flex.alignItemsCenter,
            marginLeft: theme.spacing(1),
        },
    };
});

const anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
};

const transformOrigin = {
    vertical: 'top',
    horizontal: 'left',
};

const DEFAULT_TOOLTIP = t`These are the default filters for this page`;
const SUPERCEDED_DASHBOARD_DEFAULT_TOOLTIP = t`The dashboard filters are superseded by your saved default or a shared default`;
const SUPERCEDED_SHARED_DEFAULT_TOOLTIP = t`These shared default filters are superseded by your saved default`;
const TOGGLE_DEFAULT_TOOLTIP = t`Toggle these as the default filters for this page`;
const DASHBOARD_FILTERS_HELP_TOOLTIP = t`Dashboard filters are automatically saved with the dashboard and will be used as the default filters for this page unless they are superseded by one of your saved filters or a shared default`;
const SAVED_FILTERS_HELP_TOOLTIP = t`Saved filters are your manually saved filter values that can be quickly re-applied or selected as the default filters for this page`;
const SHARED_FILTERS_HELP_TOOLTIP = t`Shared filters are filter values manually saved with this dashboard by the dashboard creator`;

export default function FilterMenu({
    classes,
    optionalFilters,
    onOptionalFilterSelected,
    savedFilterStates,
    sharedFilterStates,
    dashboardSavedFilterState,
    onSavedFilterStateSelected,
    onSaveFilterState,
    onDeleteFilterState,
    onToggleDefaultFilterState,
    showSavedFiltersProp = true,
}) {
    const defaultClasses = useStyles();
    const resolvedClasses = useMemo(() => defaults({}, classes, defaultClasses), [classes, defaultClasses]);
    const { editing } = useDashboardContext();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const sortedFilters = useMemo(
        () => sortBy(optionalFilters, [(filter) => i18n._(filter.props.title)]),
        [optionalFilters]
    );
    const showSavedFilters = showSavedFiltersProp && !editing;
    const showSharedFilters = showSavedFilters && sharedFilterStates?.length > 0;
    const hasSavedDefault = useMemo(
        () => savedFilterStates.find((savedState) => savedState.default) !== undefined,
        [savedFilterStates]
    );
    const hasAnyDefault =
        useMemo(
            () => sharedFilterStates.find((savedState) => savedState.default) !== undefined,
            [sharedFilterStates]
        ) || hasSavedDefault;

    function handleOpenMenu(event) {
        setMenuAnchor(event.currentTarget);
        event.preventDefault();
    }

    function handleCloseMenu() {
        setMenuAnchor(null);
    }

    function handleOptionalFilterClick(filter) {
        handleCloseMenu();
        onOptionalFilterSelected(filter);
    }

    function handleSavedFilterStateClick(savedFilterState) {
        handleCloseMenu();
        onSavedFilterStateSelected(savedFilterState);
    }

    function handleSaveFilterStateClick() {
        handleCloseMenu();
        onSaveFilterState();
    }

    function handleDeleteFilterStateClick(savedFilterState) {
        onDeleteFilterState(savedFilterState);
    }

    function handleToggleDefaultFilterStateClick(savedFilterState) {
        onToggleDefaultFilterState(savedFilterState);
    }

    return (
        <span className={resolvedClasses.root}>
            <IconButton
                id="optional-filter-menu-button"
                className={resolvedClasses.menuButton}
                onClick={handleOpenMenu}
                disabled={sortedFilters.length === 0 && editing}
            >
                <AddFilterIcon fontSize="small" />
            </IconButton>
            <Popover
                id="optional-filter-menu"
                className={resolvedClasses.menu}
                anchorEl={menuAnchor}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                open={menuAnchor !== null}
                onClose={handleCloseMenu}
            >
                <List>
                    {sortedFilters.length > 0 && (
                        <ListItem className={resolvedClasses.headerItem}>
                            <ListItemText>
                                <Trans>Optional Filters</Trans>
                            </ListItemText>
                        </ListItem>
                    )}
                    {sortedFilters.map((filter) => (
                        <ListItem
                            id={`optional-filter-menu-item-${filter.props.id}`}
                            key={filter.props.id}
                            onClick={() => handleOptionalFilterClick(filter)}
                        >
                            <ListItemText>{i18n._(filter.props.title)}</ListItemText>
                        </ListItem>
                    ))}
                    {showSavedFilters && dashboardSavedFilterState && (
                        <>
                            {sortedFilters.length > 0 && <Divider />}
                            <ListItem className={resolvedClasses.headerItem}>
                                <ListItemText>
                                    <Trans>Dashboard Filters</Trans>
                                    <Tooltip title={DASHBOARD_FILTERS_HELP_TOOLTIP} placement="right">
                                        <span className={resolvedClasses.helpTooltipContainer}>
                                            <HelpOutlineIcon />
                                        </span>
                                    </Tooltip>
                                </ListItemText>
                            </ListItem>
                            <ListItem
                                key={dashboardSavedFilterState.name}
                                onClick={() => handleSavedFilterStateClick(dashboardSavedFilterState)}
                            >
                                <ListItemText title={dashboardSavedFilterState.name}>
                                    {middleEllipsis(dashboardSavedFilterState.name, 75)}
                                </ListItemText>
                                <Actions className={resolvedClasses.itemActions} compact>
                                    <Action
                                        disabled
                                        icon={hasAnyDefault ? <CheckCircleOffIcon /> : <CheckCircleIcon />}
                                        tooltip={hasAnyDefault ? SUPERCEDED_DASHBOARD_DEFAULT_TOOLTIP : DEFAULT_TOOLTIP}
                                    />
                                    <Spacer />
                                </Actions>
                            </ListItem>
                        </>
                    )}
                    {showSavedFilters && (sortedFilters.length > 0 || dashboardSavedFilterState) && <Divider />}
                    {showSharedFilters && (
                        <ListItem className={resolvedClasses.headerItem}>
                            <ListItemText>
                                <Trans>Shared Filters</Trans>
                                <Tooltip title={SHARED_FILTERS_HELP_TOOLTIP} placement="right">
                                    <span className={resolvedClasses.helpTooltipContainer}>
                                        <HelpOutlineIcon />
                                    </span>
                                </Tooltip>
                            </ListItemText>
                        </ListItem>
                    )}
                    {showSharedFilters &&
                        sharedFilterStates.map((sharedState) => (
                            <ListItem key={sharedState.name} onClick={() => handleSavedFilterStateClick(sharedState)}>
                                <ListItemText title={sharedState.name}>
                                    {middleEllipsis(sharedState.name, 75)}
                                </ListItemText>
                                <Actions className={resolvedClasses.itemActions} compact>
                                    {sharedState.default ? (
                                        <Action
                                            disabled
                                            icon={hasSavedDefault ? <CheckCircleOffIcon /> : <CheckCircleIcon />}
                                            tooltip={
                                                hasSavedDefault ? SUPERCEDED_SHARED_DEFAULT_TOOLTIP : DEFAULT_TOOLTIP
                                            }
                                        />
                                    ) : (
                                        <Spacer />
                                    )}
                                    <Spacer />
                                </Actions>
                            </ListItem>
                        ))}
                    {showSharedFilters && <Divider />}
                    {showSavedFilters && (
                        <ListItem className={resolvedClasses.headerItem}>
                            <ListItemText>
                                <Trans>My Saved Filters</Trans>
                                <Tooltip title={SAVED_FILTERS_HELP_TOOLTIP} placement="right">
                                    <span className={resolvedClasses.helpTooltipContainer}>
                                        <HelpOutlineIcon />
                                    </span>
                                </Tooltip>
                            </ListItemText>
                            <Actions className={resolvedClasses.itemActions} compact>
                                <Action
                                    id="filter-menu-action-save-filter-state"
                                    icon={<SaveIcon />}
                                    tooltip={t`Save the currently applied filters to your saved filters`}
                                    onClick={() => handleSaveFilterStateClick()}
                                />
                            </Actions>
                        </ListItem>
                    )}
                    {showSavedFilters &&
                        savedFilterStates.map((savedState) => (
                            <ListItem key={savedState.name} onClick={() => handleSavedFilterStateClick(savedState)}>
                                <ListItemText title={savedState.name}>
                                    {middleEllipsis(savedState.name, 75)}
                                </ListItemText>
                                <Actions className={resolvedClasses.itemActions} compact>
                                    <Action
                                        id="filter-menu-action-set-default-filter-state"
                                        icon={savedState.default ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                        tooltip={TOGGLE_DEFAULT_TOOLTIP}
                                        onClick={() => handleToggleDefaultFilterStateClick(savedState)}
                                    />
                                    <Action
                                        id="filter-menu-action-delete-filter-state"
                                        icon={<DeleteIcon />}
                                        tooltip={t`Delete these saved filters`}
                                        onClick={() => handleDeleteFilterStateClick(savedState)}
                                    />
                                </Actions>
                            </ListItem>
                        ))}
                </List>
            </Popover>
        </span>
    );
}

FilterMenu.propTypes = {
    optionalFilters: arrayOf(node),
    onOptionalFilterSelected: func,
    savedFilterStates: arrayOf(
        shape({
            name: string.isRequired,
            default: bool,
            filterState: object,
            filterStateSummary: object,
        })
    ),
    sharedFilterStates: arrayOf(
        shape({
            name: string.isRequired,
            default: bool,
            filterState: object,
            filterStateSummary: object,
        })
    ),
    onSavedFilterStateSelected: func,
    onSaveFilterState: func,
    onDeleteFilterState: func,
    onToggleDefaultFilterState: func,
};

FilterMenu.defaultProps = {
    optionalFilters: [],
    onOptionalFilterSelected: noop,
    savedFilterStates: [],
    sharedFilterStates: [],
    onSavedFilterStateSelected: noop,
    onSaveFilterState: noop,
    onDeleteFilterState: noop,
    onToggleDefaultFilterState: noop,
};
